import { useState } from 'react'
import { T } from '../styles/theme'
import { BottomSheet, Button, Input } from './ui'
import { readImportZip, importClinicalData, importFoodDiary } from '../services/importService'

const STEP_FILE = 'file'
const STEP_PREVIEW = 'preview'
const STEP_DONE = 'done'

function errorBox(msg) {
  return (
    <div style={{ background: 'rgba(248,113,113,0.12)', border: `1px solid ${T.danger}`, borderRadius: 10, padding: '10px 14px', fontSize: 13, color: T.danger, marginBottom: 16 }}>
      {msg}
    </div>
  )
}

export default function ImportModal({ mode, myPatients, patientId, profesionalId, open, onClose, onImported }) {
  const [step, setStep] = useState(STEP_FILE)
  const [file, setFile] = useState(null)
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [matches, setMatches] = useState([])
  const [progress, setProgress] = useState(null)
  const [summary, setSummary] = useState([])

  function reset() {
    setStep(STEP_FILE)
    setFile(null)
    setPassword('')
    setError('')
    setLoading(false)
    setMatches([])
    setProgress(null)
    setSummary([])
  }

  function handleClose() {
    if (loading) return
    reset()
    onClose()
  }

  async function handleRead() {
    if (!file) { setError('Selecciona un archivo .zip'); return }
    if (!password) { setError('Introduce la contraseña'); return }
    setError('')
    setLoading(true)
    try {
      const imported = await readImportZip(file, password)
      let built
      if (mode === 'patient') {
        built = imported
          .filter(imp => imp.patient?.id === patientId)
          .map(imp => ({ imp, target: { id: patientId, nombre: imp.patient?.nombre }, selected: true }))
        if (built.length === 0) {
          setError('Este archivo no contiene tu copia de seguridad')
          setLoading(false)
          return
        }
      } else {
        const byId = new Map((myPatients ?? []).map(p => [p.id, p]))
        built = imported.map(imp => {
          const target = byId.get(imp.patient?.id) ?? null
          return { imp, target, selected: !!target }
        })
      }
      setMatches(built)
      setStep(STEP_PREVIEW)
    } catch (e) {
      setError(e.message)
    }
    setLoading(false)
  }

  function toggle(i) {
    setMatches(m => m.map((row, idx) => idx === i ? { ...row, selected: !row.selected } : row))
  }

  async function handleImport() {
    setLoading(true)
    setError('')
    const chosen = matches.filter(m => m.selected && m.target)
    const results = []
    try {
      for (const row of chosen) {
        setProgress({ nombre: row.target.nombre, phase: 'clinico' })
        const counts = await importClinicalData(row.imp, row.target.id, profesionalId)
        let foodCounts = null
        if (mode === 'patient') {
          setProgress({ nombre: row.target.nombre, phase: 'comidas' })
          foodCounts = await importFoodDiary(row.imp, row.target.id, (done, total) => setProgress({ nombre: row.target.nombre, phase: 'comidas', done, total }))
        }
        results.push({ nombre: row.target.nombre, counts, foodCounts })
      }
      setSummary(results)
      setStep(STEP_DONE)
      onImported?.()
    } catch (e) {
      setError('Error al importar: ' + e.message)
    }
    setProgress(null)
    setLoading(false)
  }

  if (!open) return null

  const selectedCount = matches.filter(m => m.selected).length

  return (
    <BottomSheet open={open} onClose={handleClose}>
      {step === STEP_FILE && (
        <>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>Importar copia de seguridad</div>
          <div style={{ fontSize: 13, color: T.muted, marginBottom: 16, lineHeight: 1.5 }}>
            Selecciona un archivo .zip exportado antes desde NutriControl.
            {mode === 'professional'
              ? ' Solo se restaura para pacientes que ya están en tu lista: nunca se crean pacientes nuevos ni se sobrescribe lo que ya tienes.'
              : ' Se restaura únicamente tu propia copia, y nunca se sobrescribe lo que ya tienes.'}
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 13, color: T.muted, marginBottom: 6 }}>Archivo (.zip)</div>
            <input type="file" accept=".zip" onChange={e => setFile(e.target.files[0] ?? null)}
              style={{ width: '100%', color: T.text, fontSize: 13 }} />
          </div>
          <Input label="Contraseña del archivo" type="password" placeholder="La que pusiste al exportar" value={password} onChange={e => setPassword(e.target.value)} />
          {error && errorBox(error)}
          <Button onClick={handleRead} disabled={loading} style={{ marginTop: 4 }}>
            {loading ? 'Leyendo...' : 'Leer archivo'}
          </Button>
        </>
      )}

      {step === STEP_PREVIEW && (
        <>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>¿Qué quieres importar?</div>
          <div style={{ fontSize: 13, color: T.muted, marginBottom: 16, lineHeight: 1.5 }}>
            Se añade solo lo que falte (mediciones, comidas, notas...); nunca se duplica ni se pisa lo que ya tienes.
          </div>
          <div style={{ maxHeight: 220, overflowY: 'auto', marginBottom: 16, border: `1px solid ${T.cardB}`, borderRadius: 12 }}>
            {matches.map((row, i) => (
              <label key={i} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                borderBottom: `1px solid ${T.cardB}`, cursor: row.target ? 'pointer' : 'default',
                opacity: row.target ? 1 : 0.5,
              }}>
                <input type="checkbox" checked={row.selected} disabled={!row.target} onChange={() => toggle(i)} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{row.imp.patient?.nombre ?? 'Paciente'}</div>
                  <div style={{ fontSize: 11, color: row.target ? T.accent : T.danger, marginTop: 1 }}>
                    {row.target ? 'Coincide con tu lista' : 'No está en tu lista — se omite'}
                  </div>
                </div>
              </label>
            ))}
          </div>
          {error && errorBox(error)}
          {progress && (
            <div style={{ fontSize: 13, color: T.muted, marginBottom: 12 }}>
              Importando {progress.nombre}
              {progress.phase === 'comidas'
                ? (progress.total ? ` · comidas ${progress.done}/${progress.total}` : ' · comidas...')
                : '...'}
            </div>
          )}
          <Button onClick={handleImport} disabled={loading || selectedCount === 0}>
            {loading ? 'Importando...' : `Importar (${selectedCount})`}
          </Button>
        </>
      )}

      {step === STEP_DONE && (
        <>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>✅ Importación completada</div>
          {summary.map((s, i) => (
            <div key={i} style={{ background: T.card, border: `1px solid ${T.cardB}`, borderRadius: 12, padding: 14, marginBottom: 10 }}>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{s.nombre}</div>
              <div style={{ fontSize: 12, color: T.muted, lineHeight: 1.6 }}>
                {s.counts.metrics} mediciones · {s.counts.skinfolds} pliegues · {s.counts.activity} actividades · {s.counts.notes} notas
                {s.counts.goals ? ' · objetivos restaurados' : ''}
                {s.counts.profile ? ' · perfil de actividad restaurado' : ''}
                {s.foodCounts ? ` · ${s.foodCounts.logs} días de comidas` : ''}
              </div>
            </div>
          ))}
          <Button onClick={handleClose} variant="soft">Cerrar</Button>
        </>
      )}
    </BottomSheet>
  )
}
